import { createReducer } from '@reduxjs/toolkit';

import * as requestActions from './requestActions.js';

const initialState = {
  requesting: false,
  error: null,

  id: 0,
  uid: 0,
  sid: 0,
  type: 'requests',
  isActive: false,
  isAdded: false,

  items: [],
  headingTitle: '',
  headingImage: '',
  headingTextAlign: 'left',
  headingDescription: '',
  headingDivider: '',

  sectionIsBackground: false,
  sectionTextColor: '#000000',
  sectionShadow: 'soft',
  sectionShadowColor: '#000',
  sectionShadowBorderColor: '#ECECF0',
  sectionBorderColor: '#ECECF0',
  sectionShape: 'round',
  sectionOpacity: 0,
  sectionBackgroundColor: '#ffffff',
  sectionIsFullWidth: false,
  sectionPadding: 20,
  sectionMargin: 12,
  sectionBackgroundType: 1,
  sectionBackgroundImage: '',
  sectionGradientColor1: '#B3F7EF',
  sectionGradientColor2: '#F7D6E0',
  sectionGradientDirection: 0,
  sectionGradientOffset: 0,
  sectionBorder: 'none',

  itemShadow: 'none',
  itemShadowColor: '#000',
  itemBorder: 'none',
  itemBorderColor: '#ECECF0',
  itemShape: 'round',
  itemOpacity: 0,

  buttonText: 'services',
  buttonBackgroundColor: '#000000',
  buttonTextColor: '#ffffff',
  buttonShadow: 'none',
  buttonShadowColor: '#000',
  buttonBorder: 'none',
  buttonBorderColor: '#ECECF0',
  buttonShape: 'round',
  buttonOpacity: 0,

  view: 'card',
};

const request = createReducer(initialState, (builder) => {
  builder
    .addCase(requestActions.requestUpdateId, (state, { payload: id }) => ({ ...state, id }))
    .addCase(requestActions.requestUpdateIsAdded, (state, { payload: isAdded }) => ({ ...state, isAdded }))
    .addCase(requestActions.requestUpdateIsActive, (state, { payload: isActive }) => ({ ...state, isActive }))

    .addCase(requestActions.requestStore, (state, { payload }) => {
      const { id, uid, sid, isActive, isAdded, data } = payload;

      const section = data ?? {};
      const {
        items,
        view,
        headingTitle,
        headingDescription,
        headingImage,
        headingTextAlign,
        headingDivider,

        sectionBackgroundColor,
        sectionTextColor,
        sectionOpacity,
        sectionIsFullWidth,
        sectionShadow,
        sectionShadowColor,
        sectionShadowBorderColor,
        sectionPadding,
        sectionMargin,
        isSectionBackground,
        sectionBackgroundType,
        sectionBackgroundImage,
        sectionGradientColor1,
        sectionGradientColor2,
        sectionGradientDirection,
        sectionGradientOffset,
        sectionShape,
        sectionBorderColor,
        sectionBorder,

        itemShadow,
        itemShadowColor,
        itemShape,
        itemBorder,
        itemBorderColor,

        buttonText,
        buttonBackgroundColor,
        buttonTextColor,
        buttonOpacity,
        buttonBorder,
        buttonBorderColor,
        buttonShadow,
        buttonShadowColor,
        buttonShape,
      } = section;

      return {
        ...state,

        error: null,
        requesting: false,

        id: id ?? 0,
        uid: uid ?? 0,
        sid: sid ?? 0,
        isActive,
        isAdded,

        items: items ?? [],
        view: view ?? 'card',

        buttonText,
        buttonBackgroundColor,
        buttonTextColor,
        buttonOpacity,
        buttonBorder,
        buttonBorderColor,
        buttonShadow,
        buttonShadowColor,
        buttonShape,

        headingTextAlign: headingTextAlign ?? 'center',
        headingTitle: headingTitle ?? '',
        headingDescription: headingDescription ?? '',
        headingImage: headingImage ?? '',
        headingDivider: headingDivider ?? '',

        sectionMargin: sectionMargin,
        sectionBackgroundColor: sectionBackgroundColor ?? '#ffffff',
        sectionTextColor: sectionTextColor ?? '#000000',
        sectionOpacity: sectionOpacity ?? 0,
        sectionIsFullWidth: sectionIsFullWidth ?? false,
        sectionShadow: sectionShadow ?? 'none',
        sectionShadowColor: sectionShadowColor ?? '#000',
        sectionPadding: sectionPadding,
        sectionIsBackground: isSectionBackground ?? false,
        sectionBackgroundType: sectionBackgroundType ?? 1,
        sectionBackgroundImage: sectionBackgroundImage ?? '',
        sectionGradientColor1: sectionGradientColor1 ?? '#B3F7EF',
        sectionGradientColor2: sectionGradientColor2 ?? '#F7D6E0',
        sectionGradientDirection: sectionGradientDirection ?? 0,
        sectionGradientOffset: sectionGradientOffset ?? 0,
        sectionShadowBorderColor: sectionShadowBorderColor ?? '#000',
        sectionBorderColor: sectionBorderColor ?? '#000',
        sectionShape: sectionShape ?? 'round',
        sectionBorder: sectionBorder ?? 'none',

        itemShadow,
        itemShadowColor,
        itemShape,
        itemBorder,
        itemBorderColor,
      };
    })
    .addCase(requestActions.requestClear, () => ({ ...initialState }))

    .addCase(requestActions.requestUpdateRequesting, (state, { payload }) => ({ ...state, requesting: payload, error: null }))
    .addCase(requestActions.requestUpdateRequest, (state) => ({ ...state, requesting: true, error: null }))
    .addCase(requestActions.requestUpdateError, (state, { payload }) => ({ ...state, error: payload, requesting: false }))
    .addCase(requestActions.requestUpdateStore, (state) => ({ ...state, error: false, requesting: false }))

    .addCase(requestActions.requestUpdateItems, (state, { payload: items }) => ({ ...state, items }))
    .addCase(requestActions.requestUpdateViewType, (state, { payload: view }) => ({ ...state, view }))

    .addCase(requestActions.requestUpdateHeadingTitle, (state, { payload: headingTitle }) => ({ ...state, headingTitle }))
    .addCase(requestActions.requestUpdateHeadingDescription, (state, { payload: headingDescription }) => ({ ...state, headingDescription }))
    .addCase(requestActions.requestUpdateHeadingTextAlign, (state, { payload: headingTextAlign }) => ({ ...state, headingTextAlign }))
    .addCase(requestActions.requestUpdateHeadingImage, (state, { payload: headingImage }) => ({ ...state, headingImage }))
    .addCase(requestActions.requestUpdateHeadingDivider, (state, { payload: headingDivider }) => ({ ...state, headingDivider }))

    .addCase(requestActions.requestUpdateSectionIsBackground, (state, { payload: sectionIsBackground }) => ({ ...state, sectionIsBackground }))
    .addCase(requestActions.requestUpdateSectionTextColor, (state, { payload: sectionTextColor }) => ({ ...state, sectionTextColor }))
    .addCase(requestActions.requestUpdateSectionBackgroundColor, (state, { payload: sectionBackgroundColor }) => ({ ...state, sectionBackgroundColor }))
    .addCase(requestActions.requestUpdateSectionOpacity, (state, { payload: sectionOpacity }) => ({ ...state, sectionOpacity }))
    .addCase(requestActions.requestUpdateSectionIsFullWidth, (state, { payload: sectionIsFullWidth }) => ({ ...state, sectionIsFullWidth }))
    .addCase(requestActions.requestUpdateSectionShadow, (state, { payload: sectionShadow }) => ({ ...state, sectionShadow }))
    .addCase(requestActions.requestUpdateSectionShadowColor, (state, { payload: sectionShadowColor }) => ({ ...state, sectionShadowColor }))
    .addCase(requestActions.requestUpdateSectionPadding, (state, { payload: sectionPadding }) => ({ ...state, sectionPadding }))
    .addCase(requestActions.requestUpdateSectionMargin, (state, { payload: sectionMargin }) => ({ ...state, sectionMargin }))
    .addCase(requestActions.requestUpdateSectionBackgroundType, (state, { payload: sectionBackgroundType }) => ({ ...state, sectionBackgroundType }))
    .addCase(requestActions.requestUpdateSectionBackgroundImage, (state, { payload: sectionBackgroundImage }) => ({ ...state, sectionBackgroundImage }))
    .addCase(requestActions.requestUpdateSectionGradientColor1, (state, { payload: sectionGradientColor1 }) => ({ ...state, sectionGradientColor1 }))
    .addCase(requestActions.requestUpdateSectionGradientColor2, (state, { payload: sectionGradientColor2 }) => ({ ...state, sectionGradientColor2 }))
    .addCase(requestActions.requestUpdateSectionGradientDirection, (state, { payload: sectionGradientDirection }) => ({ ...state, sectionGradientDirection }))
    .addCase(requestActions.requestUpdateSectionGradientOffset, (state, { payload: sectionGradientOffset }) => ({ ...state, sectionGradientOffset }))
    .addCase(requestActions.requestUpdateSectionShadowBorderColor, (state, { payload: sectionShadowBorderColor }) => ({ ...state, sectionShadowBorderColor }))
    .addCase(requestActions.requestUpdateSectionBorderColor, (state, { payload: sectionBorderColor }) => ({ ...state, sectionBorderColor }))
    .addCase(requestActions.requestUpdateSectionShape, (state, { payload: sectionShape }) => ({ ...state, sectionShape }))
    .addCase(requestActions.requestUpdateSectionBorder, (state, { payload: sectionBorder }) => ({ ...state, sectionBorder }))

    .addCase(requestActions.requestUpdateItemShadow, (state, { payload: itemShadow }) => ({ ...state, itemShadow }))
    .addCase(requestActions.requestUpdateItemShadowColor, (state, { payload: itemShadowColor }) => ({ ...state, itemShadowColor }))
    .addCase(requestActions.requestUpdateItemShape, (state, { payload: itemShape }) => ({ ...state, itemShape }))
    .addCase(requestActions.requestUpdateItemBorder, (state, { payload: itemBorder }) => ({ ...state, itemBorder }))
    .addCase(requestActions.requestUpdateItemBorderColor, (state, { payload: itemBorderColor }) => ({ ...state, itemBorderColor }))

    .addCase(requestActions.requestUpdateButtonText, (state, { payload: buttonText }) => ({ ...state, buttonText }))
    .addCase(requestActions.requestUpdateButtonTextColor, (state, { payload: buttonTextColor }) => ({ ...state, buttonTextColor }))
    .addCase(requestActions.requestUpdateButtonBackgroundColor, (state, { payload: buttonBackgroundColor }) => ({ ...state, buttonBackgroundColor }))
    .addCase(requestActions.requestUpdateButtonOpacity, (state, { payload: buttonOpacity }) => ({ ...state, buttonOpacity }))
    .addCase(requestActions.requestUpdateButtonBorder, (state, { payload: buttonBorder }) => ({ ...state, buttonBorder }))
    .addCase(requestActions.requestUpdateButtonBorderColor, (state, { payload: buttonBorderColor }) => ({ ...state, buttonBorderColor }))
    .addCase(requestActions.requestUpdateButtonShadow, (state, { payload: buttonShadow }) => ({ ...state, buttonShadow }))
    .addCase(requestActions.requestUpdateButtonShadowColor, (state, { payload: buttonShadowColor }) => ({ ...state, buttonShadowColor }))
    .addCase(requestActions.requestUpdateButtonShape, (state, { payload: buttonShape }) => ({ ...state, buttonShape }));
});

export default request;
